// @flow
import type { Action } from 'app/common/redux/types';
import type { ActivityItemType } from 'app/common/redux/reducers/activityItems';
import type { BoardPrivacy } from 'app/packages/pinterest-flow-types/board';
import type { PinImage } from 'app/common/react/components/partner/BoardUpsell/BoardUpsellFirstPin';
import type { ResourceName } from 'app/common/redux/actions/resourceFetch';
import type { UnifiedComment } from 'app/packages/pinterest-flow-types/comment';
import { profileBoardsFeedId } from 'app/common/redux/selectors/profileBoards';
import type { Story, SeasonalStoryType } from 'app/packages/pinterest-flow-types/story';

const SEASONAL_SEARCH_CONTENT_STORY_TYPES: SeasonalStoryType[] = [
  'search_banner_story',
  'search_articles_story',
  'hashtag_pins_story',
];

export type EditableFeedItemType =
  | 'pin'
  | 'board_section'
  | 'boardactivity'
  | 'boardactivitycomment'
  | 'user'
  | 'userdiditdata'
  | 'board'
  | 'aggregatedcomment';

export type FeedItemKeyProfileBoard = {|
  id: string,
  type: 'board',
  onProfile: true,
  profileGroup: BoardPrivacy | 'archived',
|};

type FeedItemKeyBoard =
  | {|
      id: string,
      type: 'board',
      onProfile: false,
    |}
  | FeedItemKeyProfileBoard;

type BoardActivityFeedKey = { id: string, type: 'boardactivity' };

type BoardActivityCommentFeedKey = { id: string, type: 'boardactivitycomment' };

export type BoardActivityUnionFeedKey = BoardActivityFeedKey | BoardActivityCommentFeedKey;

// TODO(yansun): not sure why there's FeedItemKey and FeedItem that has nearly the same definition
//   should clean this up
export type FeedItemKey =
  | FeedItemKeyBoard
  | {
      id: string,
      type: 'story',
      story_type: string,
    }
  | {
      id: string,
      type: 'module',
      name: string,
    }
  | BoardActivityFeedKey
  | BoardActivityCommentFeedKey
  | {
      id: string,
      type: 'pin',
    }
  | {
      id: string,
      type: 'board_section',
    }
  | {
      id: string,
      type: 'aggregatedcomment',
    }
  | {
      id: string,
      type: 'userdiditdata',
    }
  | {
      id: string,
      type: 'user',
    }
  | {
      id: string,
      type: 'board_first_pin_slot',
      image: PinImage,
      description: string,
      pinId: string,
      saves: number,
      impressions: number,
      link: ?string,
    };

export type FeedItemPin = {
  id: string,
  type: 'pin',
};

export type FeedItemBoardSection = {
  id: string,
  type: 'board_section',
};

export type FeedItemProfileBoard = {
  id: string,
  type: 'board',
  privacy: BoardPrivacy,
  archived_by_me_at: string,
};

type FeedItemModule = {
  id: string,
  type: 'module',
  name: string,
};

export type FeedItemActivityItem = {
  id: string,
  type: ActivityItemType,
  sticky: boolean,
};

export type FeedItemUser = {
  id: string,
  type: 'user',
};

export type FeedItemUserDidItDataItem = {
  id: string,
  type: 'userdiditdata',
};

export type FeedItemAggregatedComment = {
  id: string,
  type: 'aggregatedcomment',
};

type FeedItemBoardFirstPinSlot = {
  id: string,
  pin_id: string,
  type: 'board_first_pin_slot',
  description: string,
  image: PinImage,
  saves: number,
  impressions: number,
  link: string,
};

export type FeedItemUnifiedCommentsPreview = {|
  type: 'unifiedcommentspreview',
  unified_comment: UnifiedComment,
|};

export type FeedItem =
  | FeedItemActivityItem
  | FeedItemAggregatedComment
  | FeedItemBoardSection
  | FeedItemModule
  | FeedItemPin
  | FeedItemUser
  | FeedItemUserDidItDataItem
  | FeedItemProfileBoard
  | FeedItemBoardFirstPinSlot
  | FeedItemUnifiedCommentsPreview
  | Story;

type FeedItemKeysNormalized = {
  [id: string]: ?Array<FeedItemKey>,
};

// NOTE(yansun): FEED_TYPE naming convention = "<container-type>_<item-type>"
//   but can't assume the content will all be <item-type> (ex. BOARD_PINS feed contains stories too)
export const FEED_TYPE = {
  AGGREGATED_COMMENTS: 'aggregatedComments',
  BOARD_ACTIVITYITEMS: 'boardActivityItems',
  BOARD_ACTIVITYCOMMENTS: 'boardActivityComments',
  BOARD_PINS: 'boardPins',
  BOARD_SECTIONS: 'boardSections',
  HOMEFEED_MORE_IDEAS_TABS: 'homefeedMoreIdeasTabs',
  PROFILE_BOARDS: 'profileBoards',
  SEARCH_PINS: 'searchPins',
  SECTION_PINS: 'sectionPins',
  UNIFIED_COMMENTS: 'unifiedComments',
  USER_DID_IT_DATA: 'userDidItData',
};

export type FeedType =
  | 'aggregatedComments'
  | 'boardActivityItems'
  | 'boardActivityComments'
  | 'boardPins'
  | 'boardSections'
  | 'homefeedMoreIdeasTabs'
  | 'profileBoards'
  | 'searchPins'
  | 'sectionPins'
  | 'userDidItData'
  | 'unifiedComments';

export type State = {
  aggregatedComments: FeedItemKeysNormalized,
  boardActivityItems: FeedItemKeysNormalized,
  boardActivityComments: FeedItemKeysNormalized,
  boardPins: FeedItemKeysNormalized,
  boardSections: FeedItemKeysNormalized,
  homefeedMoreIdeasTabs: FeedItemKeysNormalized,
  profileBoards: FeedItemKeysNormalized,
  searchPins: FeedItemKeysNormalized,
  sectionPins: FeedItemKeysNormalized,
  unifiedComments: FeedItemKeysNormalized,
  userDidItData: FeedItemKeysNormalized,
};

type Normalizers = {
  [ResourceName]: (payload: { options: Object, data: ?Object }) => {
    type: FeedType,
    id: string,
    items?: Array<FeedItem>,
    reversed?: boolean,
  },
};

const FEED_NORMALIZERS: Normalizers = {
  AggregatedActivityFeedResource: ({
    options: { aggregated_pin_data_id: aggregatedPinDataId },
  }) => ({
    type: FEED_TYPE.USER_DID_IT_DATA,
    id: aggregatedPinDataId,
  }),
  AggregatedCommentFeedResource: ({ options: { objectId } }) => ({
    type: FEED_TYPE.AGGREGATED_COMMENTS,
    id: objectId,
  }),
  AggregatedCommentReplyFeedResource: ({ options: { objectId } }) => ({
    type: FEED_TYPE.AGGREGATED_COMMENTS,
    id: objectId,
    reversed: true,
  }),
  BoardActivityFeedResource: ({ options: { board_id } }) => ({
    type: FEED_TYPE.BOARD_ACTIVITYITEMS,
    id: board_id,
  }),
  BoardActivityCommentFeedResource: ({ options: { activityId, parentType } }) => ({
    type: FEED_TYPE.BOARD_ACTIVITYCOMMENTS,
    id: activityId,
    reversed: parentType === 'boardactivitycomment', // BOARD_ACTIVITY_OBJECT_TYPES.BOARD_ACTIVITY_COMMENT
  }),
  BoardFeedResource: ({ options: { board_id } }) => ({
    type: FEED_TYPE.BOARD_PINS,
    id: board_id,
  }),
  BoardSectionPinsResource: ({ options: { section_id } }) => ({
    type: FEED_TYPE.SECTION_PINS,
    id: section_id,
  }),
  BoardSectionsResource: ({ options: { board_id } }) => ({
    type: FEED_TYPE.BOARD_SECTIONS,
    id: board_id,
  }),
  BoardsResource: ({ options: { username, sort } }) => ({
    type: FEED_TYPE.PROFILE_BOARDS,
    id: profileBoardsFeedId(username, sort),
  }),
  BaseSearchResource: ({
    options: { auto_correction_disabled, scope, filters, query_pin_sigs, query, user },
    data,
  }) => ({
    type: FEED_TYPE.SEARCH_PINS,
    id: auto_correction_disabled
      ? `auto-correction-disabled:${scope}:${filters || ''}:${user || ''}:${query_pin_sigs ||
          ''}:${query}`
      : `${scope}:${filters || ''}:${user || ''}:${query_pin_sigs || ''}:${query}`,
    items:
      (data &&
        data.results &&
        data.results.filter(
          (result) =>
            !(result.story_type && SEASONAL_SEARCH_CONTENT_STORY_TYPES.includes(result.story_type)),
        )) ||
      [],
  }),
  DidItCommentsResource: ({ options: { objectId } }) => ({
    type: FEED_TYPE.AGGREGATED_COMMENTS,
    id: objectId,
    reversed: true,
  }),
  DidItUserFeedResource: ({ options: { username } }) => ({
    type: FEED_TYPE.USER_DID_IT_DATA,
    id: username,
  }),
  MoreIdeasTabsResource: () => ({
    type: FEED_TYPE.HOMEFEED_MORE_IDEAS_TABS,
    id: 'hf',
  }),
  UnifiedCommentsPreviewResource: ({ options: { aggregated_pin_id } }) => ({
    type: FEED_TYPE.UNIFIED_COMMENTS,
    id: aggregated_pin_id,
  }),
  UnifiedCommentsResource: ({ options: { aggregated_pin_id } }) => ({
    type: FEED_TYPE.UNIFIED_COMMENTS,
    id: aggregated_pin_id,
  }),
};

const initialState = {
  aggregatedComments: {},
  boardActivityItems: {},
  boardActivityComments: {},
  boardPins: {},
  boardSections: {},
  homefeedMoreIdeasTabs: {},
  profileBoards: {},
  searchPins: {},
  sectionPins: {},
  unifiedComments: {},
  userDidItData: {},
};

export function reorderItems<T>(items: Array<T>, source: number, target: number): Array<T> {
  const reorderedItems = [...items];
  // (1) remove the item being reordered from the list
  const itemReordered: T = reorderedItems.splice(source, 1)[0];
  // (2) insert the item being reordered to before the target index
  reorderedItems.splice(target, 0, itemReordered);
  return reorderedItems;
}

const feedItemToKey = (item: FeedItem, feedType: FeedType): FeedItemKey => {
  switch (item.type) {
    case 'story':
      return {
        id: item.id,
        type: 'story',
        story_type: item.story_type,
      };
    case 'module':
      return {
        id: item.id,
        type: 'module',
        name: item.name,
      };
    case 'user':
      return {
        id: item.id,
        type: 'user',
      };
    case 'board':
      return feedType === FEED_TYPE.PROFILE_BOARDS
        ? {
            id: item.id,
            type: 'board',
            onProfile: true,
            profileGroup: item.archived_by_me_at ? 'archived' : item.privacy || 'public',
          }
        : {
            id: item.id,
            type: 'board',
            onProfile: false,
          };
    case 'boardactivity':
      return {
        id: item.id,
        type: 'boardactivity',
      };
    case 'boardactivitycomment':
      return {
        id: item.id,
        type: 'boardactivitycomment',
      };
    case 'board_section':
      return {
        type: 'board_section',
        id: item.id,
      };
    case 'userdiditdata':
      return {
        type: 'userdiditdata',
        id: item.id,
      };
    case 'aggregatedcomment':
      return {
        type: 'aggregatedcomment',
        id: item.id,
      };
    case 'board_first_pin_slot':
      return {
        type: 'board_first_pin_slot',
        pinId: item.pin_id,
        id: item.id,
        image: item.image,
        description: item.description,
        saves: item.saves,
        impressions: item.impressions,
        link: item.link,
      };
    case 'unifiedcommentspreview':
      return item.unified_comment.type === 'userdiditdata'
        ? {
            type: 'userdiditdata',
            id: item.unified_comment.id,
          }
        : {
            type: 'aggregatedcomment',
            id: item.unified_comment.id,
          };
    default:
      return {
        type: 'pin',
        id: item.id,
      };
  }
};

const feedsReducer = (state: State = initialState, action: Action): State => {
  switch (action.type) {
    case 'RESOURCE_FETCH_COMPLETE':
    case 'RESOURCE_FETCH_MORE_COMPLETE': {
      const { payload } = action;
      // Resource must have flag redux_normalize_feed set in order for the normalization to take place. Utilizing a
      // whitelist method for allowing normalization, since in some cases the same resource call may not actually
      // contain a feed (i.e. when updating a board cover image)
      if (!payload.options.redux_normalize_feed) {
        return state;
      }

      const { type: feedType, id: feedId, items, reversed } = FEED_NORMALIZERS[payload.name](
        payload,
      );
      let fetchedItems = (items || payload.data || []).map((item) => feedItemToKey(item, feedType));
      if (reversed) {
        fetchedItems = fetchedItems.reverse();
      }
      const existingFeed = state[feedType][feedId];
      // don't add in items from `RESOURCE_FETCH_MORE_COMPLETE` when there's no existing feed
      //   this could be a fetch more that returned after the feed has been invalidated
      if (existingFeed || action.type !== 'RESOURCE_FETCH_MORE_COMPLETE') {
        const existingItems = existingFeed || [];
        let nextItems = fetchedItems;
        if (action.type === 'RESOURCE_FETCH_MORE_COMPLETE') {
          nextItems = reversed
            ? fetchedItems.concat(existingItems)
            : existingItems.concat(fetchedItems);
        }
        return {
          ...state,
          [feedType]: {
            ...state[feedType],
            [feedId]: nextItems,
          },
        };
      }
      break;
    }
    case 'FEED_ITEM_REORDERED': {
      const {
        payload: { feedType, feedId, itemType, targetItemId, sourceItemId },
      } = action;
      const feeds = state[feedType] || {};
      const feedItems = feeds[feedId] || [];
      let source = -1;
      let target = -1;
      if (
        [
          FEED_TYPE.BOARD_PINS,
          FEED_TYPE.SECTION_PINS,
          FEED_TYPE.BOARD_SECTIONS,
          'profileBoards',
        ].includes(feedType)
      ) {
        source = feedItems.findIndex((item) => item.type === itemType && item.id === sourceItemId);
        target = feedItems.findIndex((item) => item.type === itemType && item.id === targetItemId);
      }
      if (source !== -1 && target !== -1) {
        return {
          ...state,
          [feedType]: {
            ...feeds,
            [feedId]: reorderItems(feedItems, source, target),
          },
        };
      }
      break;
    }
    case 'FEED_ITEMS_REMOVED': {
      const {
        payload: { feedType, feedId, inverseSelection, itemType, itemIds = [] },
      } = action;
      const feeds = state[feedType] || {};
      const feedItems = feeds[feedId];
      if (
        // double check feedItems isn't null and has something, when it's null leave it that way
        //   see `FEED_INVALIDATE` for why that's the case
        feedItems &&
        feedItems.length > 0 &&
        (feedType === FEED_TYPE.SECTION_PINS || feedType === FEED_TYPE.BOARD_PINS)
      ) {
        const nextItems = feedItems.filter((item) => {
          const isItemSelected = itemIds.includes(item.id);
          const shouldRemoveItem =
            item.type === itemType &&
            ((inverseSelection && !isItemSelected) || (!inverseSelection && isItemSelected));
          return !shouldRemoveItem;
        });
        const nextItemsHasPins = !!nextItems.find((item) => item.type === 'pin');
        return {
          ...state,
          [feedType]: {
            ...feeds,
            [feedId]: nextItemsHasPins ? nextItems : [],
          },
        };
      } else if (feedItems && feedItems.length > 0 && feedType === FEED_TYPE.BOARD_SECTIONS) {
        const nextItems = feedItems.filter((item) => {
          const isItemSelected = itemIds.includes(item.id);
          return !(item.type === itemType && isItemSelected);
        });
        return {
          ...state,
          [feedType]: {
            ...feeds,
            [feedId]: nextItems,
          },
        };
      } else if (
        feedItems &&
        feedItems.length > 0 &&
        (feedType === FEED_TYPE.BOARD_ACTIVITYITEMS ||
          feedType === FEED_TYPE.AGGREGATED_COMMENTS ||
          feedType === FEED_TYPE.BOARD_ACTIVITYCOMMENTS ||
          feedType === FEED_TYPE.USER_DID_IT_DATA)
      ) {
        // TODO(yansun): this is the exact same logic as BOARD_SECTIONS, find a way to reduce duplicate code
        const nextItems = feedItems.filter((item) => {
          const isItemSelected = itemIds.includes(item.id);
          return !(item.type === itemType && isItemSelected);
        });
        return {
          ...state,
          [feedType]: {
            ...feeds,
            [feedId]: nextItems,
          },
        };
      }
      break;
    }
    case 'FEED_ITEMS_ADDED': {
      const {
        payload: { feedType, feedId, itemType, itemIds = [], prepend },
      } = action;
      const feeds = state[feedType] || {};
      const feedItems = feeds[feedId];
      // double check feedItems isn't null and has something, when it's null leave it that way
      //   see `FEED_INVALIDATE` for why that's the case
      if (feedItems && (feedType === FEED_TYPE.SECTION_PINS || feedType === FEED_TYPE.BOARD_PINS)) {
        // new items are inserted at the begining of the feed
        const itemsToBeAdded = itemIds.map((itemId) => ({
          id: itemId,
          type: itemType,
        }));
        // on boards/sections insert pins after 1st story (add pin) and 2nd story (recommended searches) if they exist
        let insertIndex = 0;
        if ((feedItems[0] || {}).type === 'story') {
          insertIndex = 1;
        }
        if ((feedItems[1] || {}).type === 'story') {
          insertIndex = 2;
        }
        const nextFeedItems = [
          ...feedItems.slice(0, insertIndex),
          ...itemsToBeAdded,
          ...feedItems.slice(insertIndex),
        ];
        return {
          ...state,
          [feedType]: {
            ...feeds,
            [feedId]: nextFeedItems,
          },
        };
      } else if (feedItems && feedType === FEED_TYPE.BOARD_SECTIONS) {
        // TODO(yansun): the follow 3 blocks of code are roughly the same, find a way to reduce duplicate code
        // new sections are appended
        const itemsToBeAdded = itemIds.reverse().map((itemId) => ({
          id: itemId,
          type: 'board_section',
        }));
        const nextFeedItems = feedItems ? [...itemsToBeAdded, ...feedItems] : [...itemsToBeAdded];
        return {
          ...state,
          [feedType]: {
            ...feeds,
            [feedId]: nextFeedItems,
          },
        };
      } else if (
        feedType === FEED_TYPE.BOARD_ACTIVITYITEMS ||
        feedType === FEED_TYPE.AGGREGATED_COMMENTS ||
        feedType === FEED_TYPE.BOARD_ACTIVITYCOMMENTS ||
        feedType === FEED_TYPE.UNIFIED_COMMENTS
      ) {
        const itemsToBeAdded = itemIds.map((itemId) => ({
          id: itemId,
          type: itemType,
        }));
        const nextFeedItems = feedItems
          ? [...(prepend ? itemsToBeAdded : feedItems), ...(prepend ? feedItems : itemsToBeAdded)]
          : itemsToBeAdded;
        return {
          ...state,
          [feedType]: {
            ...feeds,
            [feedId]: nextFeedItems,
          },
        };
      } else if (feedType === FEED_TYPE.USER_DID_IT_DATA) {
        const itemsToBeAdded = itemIds.map((itemId) => ({
          id: itemId,
          type: itemType,
        }));
        const nextFeedItems = feedItems ? [...itemsToBeAdded, ...feedItems] : itemsToBeAdded;
        return {
          ...state,
          [feedType]: {
            ...state[feedType], // there are two feeds under this type - the user's feed and the pin feed. To avoid overwriting one we merge the existing object.
            [feedId]: nextFeedItems,
          },
        };
      }
      break;
    }
    case 'FEED_INVALIDATE': {
      const {
        payload: { feedType, feedId },
      } = action;
      const feeds = state[feedType] || {};
      if (feeds[feedId]) {
        return {
          ...state,
          [feedType]: {
            ...feeds,
            // NOTE(yansun): setting the `null` value here is passed to the components reliant on these feed items.
            //  it indicates to those components the feed items are invalidated and the resource should be refreshed.
            //  it's not possible to directly refresh the resource here is because it's not possible to know which
            //    options the resource was called with, therefore impossible to get the resource options key to directly
            //    invalidate it within the resource cache in redux.
            //  `null` value is passed instead of `undefined` (i.e. deleting the feed id key), because the connected
            //    component needs to distinguish between when the feed is being fetched for the first time, and when it's
            //    invalided. See BoardPinsFeed for why the distinction matters.
            [feedId]: null,
          },
        };
      }
      break;
    }
    default:
      break;
  }
  return state;
};

export default feedsReducer;
